import { overrideIfUndefined } from '../../util/data';
import { measureTextWidth, normalizeToArray } from '../../util/utils';
import { Option } from '../../core/option';
import { getCoordBoundingRect } from '../util/coord';
import { FONT_SIZE_LIST } from '../../theme/mobile/static/font';
import type StandardChart from '../../core/StandardChart';

function islabelWidthBeyondXWidth(
  data: string[],
  fontStyle: {
    fontSize: number;
    fontFamily?: string;
    fontWeight?: string | number;
  },
  xAxisWidth: number
) {
  const DEFAULT_MARGIN = 20;
  const labelWidth = data.reduce((prev, cur, i) => {
    return (
      prev +
      measureTextWidth(cur, fontStyle.fontSize, fontStyle.fontFamily, fontStyle.fontWeight) +
      (i === data.length - 1 ? 0 : DEFAULT_MARGIN)
    );
  }, 0);
  return labelWidth > xAxisWidth;
}

function calcMedian(start: number, end: number) {
  const TWO = 2;
  return Math.floor((start + end) / TWO);
}

// 处理x轴标签显示个数
function parseXAxisLabelCount(chart) {
  const { _option } = chart.getModel();
  const xAxisArr = normalizeToArray(_option.xAxis);
  const eCharts = chart.getECharts();
  xAxisArr.forEach((el, i) => {
    el.axisLabel = overrideIfUndefined(el.axisLabel, {});
    if (el.axisLabel.dvLayout !== undefined) {
      const splitNumber = el.axisLabel.dvLayout - 1;
      el.axisLabel.interval = (index: number) => {
        const ecModel = eCharts.getModel();
        let data = el.data;
        // 判断xAxis配置里有无data，若无，从对应series里取数据
        if (el.data === undefined) {
          const targetSeries = _option.series.find(item => {
            const xAxisIndex = item.xAxisWidth || 0;
            return xAxisIndex === i;
          });
          if (targetSeries) {
            data = targetSeries.data;
          }
        }
        let start = 0;
        let end = data.length - 1;
        const dataZoomModel = ecModel.getComponent('dataZoom');
        // 考虑到dataZoom变化的场景，从dataZoom的model里取开始索引和结束索引
        if (dataZoomModel) {
          const startValue = dataZoomModel.option.startValue;
          const endValue = dataZoomModel.option.endValue;
          start = data.findIndex((_, index) => index === startValue);
          end = data.findIndex((_, index) => index === endValue);
          if (startValue < 0) {
            start = 0;
          }
          if (endValue > data.length - 1) {
            end = data.length - 1;
          }
        }
        // 数据个数未超过阈值则都可显示
        if (end - start < el.axisLabel.dvLayoutTakeEffectGreaterThan) {
          return true;
        }
        // 计算出数据间隔，从而确定哪些数据可以显示
        const interval = Math.floor((end - start) / splitNumber);
        const tickIndexArr = [start];
        for (let i = 1; i < el.axisLabel.dvLayout - 1; i++) {
          tickIndexArr.push(start + i * interval);
        }
        tickIndexArr.push(end);
        return tickIndexArr.includes(index);
      };
    }
  });
}

function parseXAxis(chart) {
  const { _option } = chart.getModel();
  const xAxisArr = normalizeToArray(_option.xAxis);
  const chartDom = chart.getDom();
  const gridOption = _option.grid;
  const coord = getCoordBoundingRect(chartDom, gridOption);
  const eChart = chart.getECharts();

  xAxisArr.forEach((el, i) => {
    if (!el.dvSplit3 || el.axisLabel?.interval) {
      return;
    }
    const data = overrideIfUndefined(el.data, _option.series[i].data);
    el.axisLabel = overrideIfUndefined(el.axisLabel, {});
    const formatter = el.axisLabel?.formatter || (value => value);
    const formattedData = data.map(formatter);
    const fontStyle = {
      fontSize: el.axisLabel.fontSize || FONT_SIZE_LIST.axisLabel,
      fontWeight: el.axisLabel.fontWeight,
      fontFamily: el.axisLabel.fontFamily
    };
    // 如果标签文本总长度超过了grid的宽度则使用3段式布局
    if (islabelWidthBeyondXWidth(formattedData, fontStyle, coord.width)) {
      el.axisLabel.interval = (index: number) => {
        const echartModel = eChart.getModel();
        let start = 0;
        let end = data.length - 1;

        const dataZoomModel = echartModel.getComponent('dataZoom');
        if (dataZoomModel) {
          const startValue = dataZoomModel.option.startValue;
          const endValue = dataZoomModel.option.endValue;
          start = data.findIndex((_, index) => index === startValue);
          end = data.findIndex((_, index) => index === endValue);
          if (startValue < 0) {
            start = 0;
          }
          if (endValue > data.length - 1) {
            end = data.length - 1;
          }
        }
        const middle = calcMedian(start, end);
        const threeLabels = formattedData.filter(
          (_, j) => j === start || j === end || j === middle
        );
        if (islabelWidthBeyondXWidth(threeLabels, fontStyle, coord.width)) {
          return index === start || index === end;
        }
        return index === start || index === end || index === middle;
      };
    }
  });
}

function parse(option: Option, index: number, chart: StandardChart) {
  parseXAxisLabelCount(chart);
}

export default {
  type: 'xAxis',
  parse
};
